import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  getAgentSettings,
  getModels,
  saveAgentSettings,
  setFreeModel,
  type AgentSettings as AgentSettingsT,
  type OrModelOption,
  ApiRequestError,
} from "@app/lib/api";

const STYLES = ["balanced", "concise", "detailed"] as const;
type Style = (typeof STYLES)[number];
type ModelSlot = "chat" | "cheap" | "image";

const DEFAULT_SETTINGS: AgentSettingsT = {
  personaMd: "",
  modelMap: { chat: "", cheap: "", image: "" },
  timezone: "UTC",
  responseStyle: "balanced",
  freeModel: false,
};

interface ModelPickerProps {
  slot: ModelSlot;
  value: string;
  onChange: (v: string) => void;
  models: OrModelOption[] | null;
  loadError: string | null;
}

function ModelPicker({ slot, value, onChange, models, loadError }: ModelPickerProps) {
  const id = `m-${slot}`;
  if (loadError && !models) {
    return (
      <div className="flex flex-col gap-1">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste model id (catalog unavailable)"
        />
        <div className="text-xs text-amber-400">
          Catalog unavailable: {loadError}. Paste a model id manually.
        </div>
      </div>
    );
  }
  if (!models) {
    return (
      <select id={id} disabled className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-muted-fg">
        <option>Loading models...</option>
      </select>
    );
  }
  if (models.length === 0) {
    return (
      <div className="text-xs text-amber-400">
        No {slot === "image" ? "image-capable " : ""}models available in the catalog.
        {slot === "image" ? " The image task will be disabled until one is published." : ""}
      </div>
    );
  }
  const known = new Set(models.map((m) => m.id));
  const showCustom = value !== "" && !known.has(value);
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
    >
      <option value="">Select a model...</option>
      {showCustom ? <option value={value}>{`(custom) ${value}`}</option> : null}
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name ? `${m.name} - ${m.id}` : m.id}
        </option>
      ))}
    </select>
  );
}

export default function Settings() {
  const [s, setS] = useState<AgentSettingsT | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [allModels, setAllModels] = useState<OrModelOption[] | null>(null);
  const [imageModels, setImageModels] = useState<OrModelOption[] | null>(null);
  const [modelsErr, setModelsErr] = useState<string | null>(null);

  useEffect(() => {
    getAgentSettings()
      .then((v) =>
        setS({
          ...DEFAULT_SETTINGS,
          ...v,
          modelMap: { ...DEFAULT_SETTINGS.modelMap, ...v.modelMap },
        }),
      )
      .catch((e) => {
        if (e instanceof ApiRequestError && e.status === 404) {
          setS(DEFAULT_SETTINGS);
        } else {
          setErr(e instanceof Error ? e.message : String(e));
        }
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getModels(), getModels({ image: true })])
      .then(([all, image]) => {
        if (cancelled) return;
        setAllModels(all.data);
        setImageModels(image.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setModelsErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!s) {
    return <div className="text-sm text-muted-fg">Loading settings...</div>;
  }

  const onSave = async () => {
    setErr(null);
    setSaving(true);
    try {
      await saveAgentSettings(s);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onToggleFree = async (next: boolean) => {
    setErr(null);
    try {
      await setFreeModel(next);
      setS({ ...s, freeModel: next });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold">Settings</h1>

      {err ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {err}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Persona</CardTitle>
          <CardDescription>
            Plain markdown. Written to SOUL.md inside the VM at next boot.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <textarea
            className="min-h-[160px] w-full rounded-md border border-border bg-bg p-2 text-sm font-mono"
            value={s.personaMd}
            onChange={(e) => setS({ ...s, personaMd: e.target.value })}
            placeholder="You are John, a CEO co-pilot..."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Models</CardTitle>
          <CardDescription>
            OpenRouter model per task, picked from the live catalog. Free-mode
            override picks the best available free model for chat, cheap, and
            image; if no free image model exists the image task is disabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(["chat", "cheap", "image"] as const).map((k) => (
            <div key={k} className="grid grid-cols-[100px_1fr] items-center gap-3">
              <Label htmlFor={`m-${k}`} className="capitalize">
                {k}
              </Label>
              <ModelPicker
                slot={k}
                value={s.modelMap[k]}
                onChange={(v) =>
                  setS({ ...s, modelMap: { ...s.modelMap, [k]: v } })
                }
                models={k === "image" ? imageModels : allModels}
                loadError={modelsErr}
              />
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <input
              id="free"
              type="checkbox"
              checked={s.freeModel}
              onChange={(e) => onToggleFree(e.target.checked)}
            />
            <Label htmlFor="free" className="text-sm">
              Use free model (overrides chat, cheap, and image)
            </Label>
          </div>
          {s.freeModel && imageModels && !imageModels.some((m) => m.id.endsWith(":free")) ? (
            <div className="text-xs text-amber-400">
              No free image model available in the catalog right now; the image
              task will be disabled on the VM until one is published.
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Behavior</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-[140px_1fr] items-center gap-3">
            <Label htmlFor="tz">Timezone</Label>
            <Input
              id="tz"
              value={s.timezone}
              onChange={(e) => setS({ ...s, timezone: e.target.value })}
              placeholder="UTC"
            />
          </div>
          <div className="grid grid-cols-[140px_1fr] items-center gap-3">
            <Label htmlFor="style">Response style</Label>
            <select
              id="style"
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
              value={s.responseStyle}
              onChange={(e) => setS({ ...s, responseStyle: e.target.value as Style })}
            >
              {STYLES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={onSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
        {savedAt ? (
          <span className="text-xs text-muted-fg">
            Saved {new Date(savedAt).toLocaleTimeString()}
          </span>
        ) : null}
        <span className="text-xs text-muted-fg">
          Note: changes take effect at next VM boot.
        </span>
      </div>
    </div>
  );
}
